import { useGetAffiliateDashboard } from '@/api/v1/affiliation/get-affiliate-dashboard'
import { useContextStore } from '@/stores/context'

export interface AffiliateEntry {
  /**
   * Whether this session may see the program at all. It belongs to the
   * logged account: never a managed one, never a blocked one, and only its
   * administrators. Managing someone else's account does not touch it — the
   * affiliation endpoints all resolve the logged account and ignore the
   * managed-account header.
   */
  isVisible: boolean
  /**
   * Whether the account already joined. `undefined` until the gate read lands
   * — the caller navigates in that window, where the page's own landing takes
   * over.
   */
  isJoined: boolean | undefined
}

/**
 * The sidebar entry to the affiliate program, and the two questions it has to
 * answer: whether to show it, and whether clicking it opens the program or the
 * invitation to activate it.
 *
 * Reads the same `['affiliate-dashboard']` query the section gates on, so
 * opening the program from the menu finds it cached instead of replaying the
 * page skeleton.
 */
export function useAffiliateEntry(): AffiliateEntry {
  const accountContext = useContextStore((state) => state.userAccountContext)

  const isAdmin =
    accountContext?.userAccountInformation.role === 'Administrator'
  const isManaged = accountContext?.accountInformation.isManaged ?? false
  const isBlocked =
    accountContext?.accountInformation.isAffiliateProgramBlocked ?? false
  const isVisible = isAdmin && !isManaged && !isBlocked

  // Every affiliation endpoint rejects the accounts the gate above hides, so
  // the read only happens for the ones that can act on it.
  const { data } = useGetAffiliateDashboard({ enabled: isVisible })

  return { isVisible, isJoined: data?.joined }
}
